import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";

import { VerifyMfaResult } from "../api-service/auth";
import {
  getSecretQuestionChallenge,
  verifySecretQuestion,
} from "../api-service/secret-questions";
import { ROUTES, clearUserInfo } from "../common";
import messages from "../messages/ja.json";
import { Icon } from "../parts/icon";
import { Loading } from "../parts/loading";
import { RedirectOverlay } from "../parts/redirect-overlay";
import { SecretQuestionShell } from "../parts/secret-question-shell";
import { SelectMenu } from "../parts/select-menu";
import { useExternalRedirect } from "../parts/use-external-redirect";
import { useAuth } from "../provider";
import { SecretQuestionChallenge } from "../types/secret-questions";

import "../styles/auth-flow.css";
import "../styles/secret-question.css";

const m = messages.secretQuestions;

interface AnswerRow {
  questionId: string;
  answer: string;
}

interface VerifyForm {
  rows: AnswerRow[];
}

// Both halves of a slot are mandatory — the user picks one of their enrolled questions and
// types its answer. The messages match the enrollment form's.
const schema = yup.object({
  rows: yup.array().of(
    yup.object({
      questionId: yup.string().defined().required(m.questionRequired),
      answer: yup.string().trim().required(m.answerRequired),
    })
  ),
});

// Circled check on the verify button — same glyph as the face-registration action button.
const CheckCircleIcon = () => (
  <svg
    className="secret-question-submit-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12.5l2.5 2.5 4.5-5" />
  </svg>
);

// FE-2: answer an enrolled secret question when face MFA can't be used. Reached from
// /verify-mfa after enable-fallback returns 200. The challenge endpoint serves the enrolled
// questions; the user picks one per slot (the BE asks for `required_count` answers) and a
// correct answer returns the same redirect_url as verify-mfa, so the flow finishes exactly
// like the face path (back to Salesforce).
export function SecretQuestionsVerifyPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState<SecretQuestionChallenge | null>(null);
  const [loadError, setLoadError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<VerifyForm>({
    resolver: yupResolver(schema) as any,
    defaultValues: { rows: [] },
  });
  const { fields } = useFieldArray({ control, name: "rows" });

  // Every failure on this screen is reported by a toast only — nothing is written into the
  // card, which would push the form around (the challenge-fetch failure below is the one
  // exception: there is no form to keep, so the card carries the reason and the retry).
  const showError = (message: string) =>
    toast.error(message, { icon: <Icon name="warning" size={27} /> });

  // Hand-off to Salesforce. The overlay blocks the answer form while it is in flight; a
  // failed redirect clears it so the question can be answered again.
  const redirect = useExternalRedirect(showError);

  const loadChallenge = () => {
    setLoadError("");
    setChallenge(null);
    getSecretQuestionChallenge()
      .then((data) => {
        if (data.questions.length === 0) {
          setLoadError(m.challengeError);
          return;
        }
        setChallenge(data);
        // One slot per answer the BE wants (`required_count`), never more than there are
        // questions to pick from. Normally that is a single slot, as in the design.
        const slots = Math.max(1, Math.min(data.requiredCount || 1, data.questions.length));
        reset({ rows: Array.from({ length: slots }, () => ({ questionId: "", answer: "" })) });
      })
      .catch((err: any) => setLoadError(err?.error?.message || err?.message || m.challengeError));
  };

  useEffect(loadChallenge, []); // eslint-disable-line react-hooks/exhaustive-deps

  const watchedRows = watch("rows");
  const selectedIds = (watchedRows ?? []).map((row) => row?.questionId).filter(Boolean);
  // Hide questions already picked in another slot so each is answered at most once.
  const optionsFor = (index: number) =>
    (challenge?.questions ?? []).filter(
      (q) =>
        q.questionId === watchedRows?.[index]?.questionId || !selectedIds.includes(q.questionId)
    );

  const finishSuccess = (res: VerifyMfaResult) => {
    clearUserInfo();
    if (res.redirectUrl) {
      // No toast on this screen, so no reason to delay the hand-off.
      redirect.start(res.redirectUrl, 0);
      return;
    }
    setDone(true);
  };

  // The offending field can be off-screen behind the keyboard on a phone, so the toast makes
  // sure the failure is noticed; the per-field messages say what is wrong.
  const onInvalid = () => showError(m.verifyInvalid);

  const onSubmit = async (data: VerifyForm) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await verifySecretQuestion(
        data.rows.map((row) => ({ question_id: row.questionId, answer: row.answer.trim() }))
      );
      finishSuccess(res);
    } catch (err: any) {
      // Wrong answer (or a transient failure): the backend's reason goes to the toast and the
      // same questions stay on screen so the user can retry. The session survives a failed
      // verify (it is only cleared on success), so no re-fetch is needed — only the answers
      // are wiped.
      showError(err?.error?.message || err?.message || m.verifyError);
      data.rows.forEach((_, index) =>
        setValue(`rows.${index}.answer`, "", { shouldValidate: false })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting || done || redirect.redirecting;

  const footer = (
    <>
      <button
        type="button"
        className="secret-question-cancel"
        onClick={() => navigate(ROUTES.login, { replace: true })}
        disabled={busy}
      >
        {messages.face.cancel}
      </button>

      {/* Lives in the footer, outside the card's <form> — submitted through the same
          handler as pressing Enter inside a field. */}
      <button
        type="button"
        className="secret-question-submit"
        onClick={handleSubmit(onSubmit, onInvalid)}
        disabled={busy || !challenge}
      >
        <CheckCircleIcon />
        <span>{m.verify}</span>
      </button>

      {/* Empty third grid cell: keeps the button on the page centre, not on the centre of
          the space left by the cancel link. */}
      <span aria-hidden="true" />
    </>
  );

  return (
    <SecretQuestionShell
      username={auth.user?.username}
      onBackToFace={() => navigate(ROUTES.checkin, { replace: true })}
      backDisabled={busy}
      footer={footer}
    >
      <section className="secret-question-card">
        {done ? (
          <p className="secret-question-banner is-success">{messages.checkin.success}</p>
        ) : loadError ? (
          // Nothing to answer: show just the reason and a retry.
          <>
            <p className="secret-question-banner is-error">{loadError}</p>
            <button type="button" className="secret-question-retry" onClick={loadChallenge}>
              {m.statusRetry}
            </button>
          </>
        ) : !challenge ? (
          <Loading />
        ) : (
          <>
            {/* Two parts, not one string: on a phone each becomes its own line so the break
                always lands where the mock puts it, while on desktop they stay inline and
                read as one sentence (Japanese needs no space between them). */}
            <h2 className="secret-question-card-title">
              {m.verifyPromptLines.map((line) => (
                <span className="secret-question-card-title-line" key={line}>
                  {line}
                </span>
              ))}
            </h2>

            <form
              className="secret-question-form"
              onSubmit={handleSubmit(onSubmit, onInvalid)}
              noValidate
            >
              {fields.map((field, index) => (
                <div className="secret-question-slot" key={field.id}>
                  {/* secret-question-select wraps the shared SelectMenu so this screen's
                      dropdown styling is self-contained (see secret-question.css). */}
                  <div className="secret-question-field secret-question-select">
                    <span className="secret-question-label" id={`${field.id}-label`}>
                      {m.selectLabel}
                    </span>
                    <Controller
                      control={control}
                      name={`rows.${index}.questionId`}
                      render={({ field: select }) => (
                        <SelectMenu
                          name={select.name}
                          labelId={`${field.id}-label`}
                          value={select.value}
                          onChange={select.onChange}
                          onBlur={select.onBlur}
                          options={optionsFor(index).map((q) => ({
                            value: q.questionId,
                            label: q.question,
                          }))}
                          placeholder={m.selectPlaceholder}
                          disabled={busy}
                          required
                          invalid={!!errors.rows?.[index]?.questionId}
                          // Points at the message below so a screen reader reads the reason,
                          // not just "invalid".
                          describedBy={
                            errors.rows?.[index]?.questionId
                              ? `${field.id}-question-error`
                              : undefined
                          }
                        />
                      )}
                    />
                    {errors.rows?.[index]?.questionId ? (
                      <p className="secret-question-field-error" id={`${field.id}-question-error`}>
                        {errors.rows[index]?.questionId?.message}
                      </p>
                    ) : null}
                  </div>

                  <label className="secret-question-field">
                    <span className="secret-question-label">{m.answer}</span>
                    <input
                      className="secret-question-input"
                      autoComplete="off"
                      disabled={busy}
                      placeholder={m.answerPlaceholder}
                      aria-required
                      aria-invalid={!!errors.rows?.[index]?.answer}
                      aria-describedby={
                        errors.rows?.[index]?.answer ? `${field.id}-answer-error` : undefined
                      }
                      {...register(`rows.${index}.answer`)}
                    />
                    {errors.rows?.[index]?.answer ? (
                      <p className="secret-question-field-error" id={`${field.id}-answer-error`}>
                        {errors.rows[index]?.answer?.message}
                      </p>
                    ) : null}
                  </label>
                </div>
              ))}
            </form>
          </>
        )}
      </section>

      {redirect.redirecting ? <RedirectOverlay label={messages.checkin.redirecting} /> : null}
    </SecretQuestionShell>
  );
}
